import type { TicketCategoryTreeNodeDto } from "@/api/types";
import { isDefined } from "@/helpers/util";

type TicketCategoryTreeNodeWithParentDto = TicketCategoryTreeNodeDto & {
  parent?: TicketCategoryTreeNodeDto;
};

export function recursivelyEnableNode(treeNode: TicketCategoryTreeNodeDto): void {
  treeNode.enabled = true;
  recursivelyUpdateChildren(treeNode, true);
  recursivelyEnableParents(treeNode);
}

export function recursivelyDisableNode(treeNode: TicketCategoryTreeNodeDto): void {
  treeNode.enabled = false;
  recursivelyUpdateChildren(treeNode, false);
  recursivelyDisableParents(treeNode);
}

function recursivelyUpdateChildren(treeNode: TicketCategoryTreeNodeDto, enabled: boolean) {
  const maxIterations = 10000;

  const stack: TicketCategoryTreeNodeDto[] = [treeNode];
  let iteration = 0;

  while (stack.length > 0) {
    if (iteration > maxIterations) {
      if (enabled) {
        throw new Error("Iteration limit exceeded while enabling tree nodes. Potential cycle or overly deep tree.");
      } else {
        throw new Error("Iteration limit exceeded while disabling tree nodes. Potential cycle or overly deep tree.");
      }
    }
    iteration++;

    const current = stack.pop()!;

    current.enabled = enabled;

    const children = current.children || [];
    const childrenToUpdate = children.filter((child) => child.enabled !== enabled);
    for (const childToUpdate of childrenToUpdate) {
      stack.push(childToUpdate);
    }
  }
}

function recursivelyEnableParents(treeNode: TicketCategoryTreeNodeWithParentDto): void {
  const maxIterations = 1000;
  let iteration = 0;
  let current: TicketCategoryTreeNodeWithParentDto | undefined = treeNode;

  while (current.parent) {
    if (iteration > maxIterations) {
      throw new Error(
        "Iteration limit exceeded while enabling parent tree nodes. Potential cycle or overly deep tree.",
      );
    }
    iteration++;

    const parentNode: TicketCategoryTreeNodeDto = current.parent;

    parentNode.enabled = true;
    current = parentNode;
  }
}

function recursivelyDisableParents(treeNode: TicketCategoryTreeNodeWithParentDto): void {
  const maxIterations = 1000;
  let iteration = 0;
  let current: TicketCategoryTreeNodeWithParentDto | undefined = treeNode;

  while (current.parent) {
    if (iteration > maxIterations) {
      throw new Error(
        "Iteration limit exceeded while disabling parent tree nodes. Potential cycle or logic error (parent not disabled, loop continued on same node).",
      );
    }

    iteration++;

    const parentNode: TicketCategoryTreeNodeDto = current.parent;

    const parentChildren = parentNode.children || [];
    const allParentsChildrenDisabled = parentChildren.every((x) => !x.enabled);

    if (allParentsChildrenDisabled) {
      parentNode.enabled = false;
      current = parentNode;
    } else {
      break;
    }
  }
}

/**
 * In each node in the tree, sets the `.parent` property to the parent node.
 * This makes iterating through the tree a lot easier.
 * When using this function, make sure to call `cleanTreeFromParents` afterwards, as React-Query cannot handle recursive structures.
 */
export function populateTreeWithParents(
  nodes: TicketCategoryTreeNodeWithParentDto[],
  parent: TicketCategoryTreeNodeDto | undefined = undefined,
): void {
  for (const node of nodes) {
    node.parent = parent;
    if (node.children) {
      populateTreeWithParents(node.children, node);
    }
  }
}

/**
 * Removes all the `.parent` properties from the tree nodes.
 * This is necessary because React Query cannot handle recursive structures.
 */
export function cleanTreeFromParents(tree: TicketCategoryTreeNodeWithParentDto[]): void {
  for (const node of tree) {
    node.parent = undefined;
    if (node.children) {
      cleanTreeFromParents(node.children);
    }
  }
}

export function findNodeInTree<T extends TicketCategoryTreeNodeDto>(tree: T[], id: string): T | undefined {
  for (const node of tree) {
    if (node.id === id) {
      return node;
    }

    const foundInChildren = findNodeInTree(node.children || [], id);
    if (foundInChildren) {
      return foundInChildren as T;
    }
  }

  return undefined;
}

export function searchTree(
  rootNode: TicketCategoryTreeNodeDto,
  searchTerm: string,
): TicketCategoryTreeNodeDto | undefined {
  if (!searchTerm || searchTerm.trim() === "") {
    return rootNode;
  }

  const lowerCaseTerm = searchTerm.toLowerCase().trim();

  return searchTreeNode(rootNode, lowerCaseTerm);
}

function searchTreeNode(
  currentNode: TicketCategoryTreeNodeDto,
  searchTerm: string,
): TicketCategoryTreeNodeDto | undefined {
  if (!currentNode) {
    return undefined;
  }

  const isMatch = currentNode.name.toLowerCase().includes(searchTerm);
  if (isMatch) {
    return { ...currentNode, children: currentNode.children || [] };
  }

  let filteredChildren: TicketCategoryTreeNodeDto[] = [];
  if (currentNode.children && currentNode.children.length > 0) {
    filteredChildren = currentNode.children.map((child) => searchTreeNode(child, searchTerm)).filter(isDefined);
  }

  if (filteredChildren.length > 0) {
    return {
      ...currentNode,
      children: filteredChildren,
    };
  }

  return undefined;
}
